
import { Guest } from "../types/Guest";
import {
  createContext,
  useContext,
  ReactNode,
  useState,
  useRef,
  useEffect,
  useMemo,
  useCallback,
  useTransition,
} from "react";
import { useFields } from "./FieldsProvider";
import { useMetaTable } from "./MetaTableProvider";
import { useMetaRequest } from "./MetaRequestProvider";
import { useFilters } from "./FiltersProvider";
import {
  createPending,
  getGuestsToBeReviewed,
  updateGuests,
} from "@/lib/api/handlers/GuestHandling";
import {
  parseFiltersApiRequest,
  parseFieldsApiRequest,
} from "@/lib/utils/parser";
import { AcceptanceState } from "../types/enums/AcceptanceState";
import { GuestType } from "../types/enums/GuestType";
import { dropSelfWrittenFields } from "@/lib/guest-reconcile/selfWrittenFields";
import { readBackGuestVerdict } from "@/lib/guest-reconcile/readBackAcceptance";
import {
  armGuestReconcile,
  armCoarseReconcile,
  GuestReconciledDetail,
  FieldWatch,
} from "@/lib/guest-reconcile/reconcileScheduler";
import { applyServerTruth } from "@/lib/utils/guest-patch";
import { deriveLifecycleTriggers, explicitUpdateTrigger } from "@/lib/utils/derive-lifecycle-triggers";
import { useGuestAutomationGate } from "@/hooks/useGuestAutomationGate";
import { useSignatureEffect } from "../hooks/useSignatureEffect";
import { useQueryClient } from "@tanstack/react-query";
import { mergeGuestPatch } from "@/lib/utils/guest-patch";
import { COMPANION_DTO_FIELDS, restoreRow } from "@/lib/utils/rows";
import {
  addGuestToRequestsCache,
  applyServerTruthInRequestsCache,
  dropGuestFromRequestsCache,
  isPendingRequestRow,
  refetchRequestsSnapshots,
} from "@/lib/query/requestsCache";
import type { GuestDataUpdatedDetail } from "./GuestsProvider";
import {
  approvalBlockedGuestIds,
  enqueuedTriggersFor,
  isServerErrorResponse,
  capacityContextOf,
  CapacityContext,
  throwServerError,
} from "@/lib/api/errors/ErrorHandler";
import { CapacityDecisionModal } from "@/components/CapacityDecisionModal/CapacityDecisionModal";
import { describeBatchUpdateError } from "@/lib/i18n/translateBackendError";
import { useToast } from "@/components/Toast/Toast";
import { useTranslations } from "next-intl";

/**
 * What one decision actually achieved.
 *
 * `null` means the write did not go through — a blocked mode, an archived
 * event, a stale two-tab decline, or a capacity decision the modal has taken
 * over. The caller must show no success toast for it: the provider has already
 * surfaced an error, or the modal carries the next step (BUGS.md H29).
 *
 * Otherwise `triggers` is what the SERVER reports it enqueued for this guest —
 * usually empty, and empty is a real answer. It used to be a bare `true`, which
 * let the cockpit announce "automations triggered" from its own checkbox while
 * nothing had been sent at all.
 */
export type DecideOutcome = { triggers: string[] } | null;

interface RegistrantsContextProps {
  registrants: Guest[];
  loading: boolean;
  accept: (guestId: string) => Promise<DecideOutcome>;
  decline: (guestId: string) => Promise<DecideOutcome>;
  /** Every acceptance decision goes through here; `accept`/`decline` are thin
   *  aliases kept for the existing call sites. `SYSTEM` is refused — waitlist
   *  membership is the server's to assign (forceWaitlist / the promoter CAS),
   *  and a generic path into it would skip the contingent bookkeeping. */
  decide: (
    guestId: string,
    next: AcceptanceState,
    opts?: { retriggerAutomations?: boolean },
  ) => Promise<DecideOutcome>;
  errors: string | null;
  setErrors: React.Dispatch<React.SetStateAction<string | null>>;
  /** LIST-refetch failure only (pagination/filter reload). Rendered by
   *  RegistrantsCard — a failed refetch used to blank the table silently
   *  (BUGS.md M4/M12, FIXED 2026-07-05 W2-E). Mutation failures stay in
   *  `errors` (separately pinned). */
  fetchError: string | null;
  createRegistrants: (companion: Guest) => Promise<void>;
}

const RegistrantsContext = createContext<RegistrantsContextProps | undefined>(
  undefined
);


interface RegistrantsProviderProps {
  children: ReactNode;
  initialRegistrants: Guest[];
  eventId: string;
}

export const RegistrantsProvider = ({
  initialRegistrants,
  children,
  eventId,
}: RegistrantsProviderProps) => {
  // allFieldsUnfiltered (not allFields), like the count lane in
  // MetaTableProvider and the host prefetch: a CUSTOM role's allowlist hides
  // columns from display, but a saved filter on one must still be SENT —
  // dropped here, the rows came back unfiltered under a count that kept the
  // row (field-gate v2 answers it with "nobody"). Same set, same answer.
  const { allFieldsUnfiltered } = useFields();
  const allFields = allFieldsUnfiltered || [];
  const [errors, setErrors] = useState<string | null>(null);
  const [fetchError, setFetchError] = useState<string | null>(null);
  // H29: mutation failures used to land in `errors` with NO renderer — a
  // blocked/archived/stale accept-decline surfaced nowhere while the UI
  // flashed a false success. Surface them through the app toast.
  const { showToast } = useToast();
  const tErr = useTranslations("errors");
  // The WHOLE error, not its `.message`: the guest batch 400 carries no
  // top-level reason (`batchUpdatePartialFailure` = "Some guests could not be
  // saved."), the per-row code is the reason (M-01a).
  const surfaceError = useCallback(
    (error: unknown) => {
      showToast(describeBatchUpdateError(tErr, error, tErr("unknown")), "error");
    },
    [showToast, tErr],
  );
  const { activeFilterState } = useFilters();
  // `isPending` (background refetch on filter/page/archived/search change) is
  // the ONLY signal that should dim the table behind a spinner. Accept/decline/
  // create are optimistic — they mutate `registrants` in place — so they must
  // NOT raise the table's loading overlay, otherwise every action flashes the
  // whole table invisible for the network round-trip (looks like a reload).
  const [isPending, startTransition] = useTransition();
  const { archived, page, pageSize, search, setTotalCount } = useMetaTable();
  // Tab-badge count lives in MetaRequestProvider (separate from the table's
  // totalCount). Accept/decline keep it in sync from TCCAction; the create
  // path must mirror that here so the badge bumps without a reload.
  const { setCounts } = useMetaRequest();
  const [registrants, setRegistrants] = useState<Guest[]>(initialRegistrants);
  // Rollback snapshots read from HERE, not from the `registrants` closure.
  // Assigned during render so it always holds the latest committed rows: a
  // callback that closed over the state value captured the array as it looked
  // when the callback was created, which is what let a late failure undo an
  // earlier success (see restoreRow). It also lets the decision callbacks drop
  // `registrants` from their deps, so their identity stops churning on every
  // list change and the React.memo on the action cell finally holds.
  const rowsRef = useRef<Guest[]>(registrants);
  rowsRef.current = registrants;
  // Reconcile when the HOST's query delivers fresher data (the post-mutation
  // invalidation marks it stale, so a remount serves the cached seed and then
  // refetches). The signature effect below keys on request PARAMS — identical
  // params ⇒ no fetch — so without this the stale seed would pin forever and
  // an already-approved request would resurrect as an actionable row.
  // Compared by CONTENT, not identity — the host can hand down a new array
  // per render without new data (identity-churn pins).
  const initialSeedRef = useRef(JSON.stringify(initialRegistrants));
  // The host query ALWAYS holds page 1 with the mount filters. Once the user
  // has paged, searched or refiltered here, adopting that array would swap the
  // visible rows for the wrong page — so a diverged view keeps its own rows and
  // only re-baselines the seed. (Adopting unconditionally is how a filter/view
  // save used to yank a user on page 3 back to unfiltered page-1 rows while the
  // paginator still read "Seite 3".)
  const signatureRef = useRef<string | null>(null);
  const mountSignatureRef = useRef<string | null>(null);
  useEffect(() => {
    const seedSignature = JSON.stringify(initialRegistrants);
    if (initialSeedRef.current === seedSignature) return;
    initialSeedRef.current = seedSignature;
    if (signatureRef.current !== mountSignatureRef.current) return;
    setRegistrants(
      initialRegistrants.filter((g) => !recentlyMutatedIdsRef.current.has(g.guestId!)),
    );
  }, [initialRegistrants]);
  // The requests-page and guestlist query snapshots outlive this provider.
  // Without this, a remount within staleTime resurrects an already-actioned
  // request as PENDING — and a second click on the ghost row fires a real,
  // contradictory write (decline on an already-accepted guest). refetchType
  // "none": the provider's own signature refetch keeps the mounted page live.
  const queryClient = useQueryClient();
  // Cheap gate: no ACTIONS plugin, or no UPDATE_GUEST pipe on this trigger,
  // means no reconcile wave is ever started.
  const { mayRun, watchedFieldsFor } = useGuestAutomationGate(eventId);
  const invalidateRequestCaches = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ["guestsRequests", eventId], refetchType: "none" });
    void queryClient.invalidateQueries({ queryKey: ["guestlist", eventId], refetchType: "none" });
  }, [queryClient, eventId]);
  // Holds guestIds whose accept/decline was applied optimistically. The
  // refetch effect filters its server response through this set so a
  // background refetch can't put a just-actioned row back on screen
  // before the server's read-side reflects the PUT.
  const recentlyMutatedIdsRef = useRef<Set<string>>(new Set());
  // The shield answers "did this row leave the list I am looking at?", so it is
  // only valid for ONE view. The archive toggle flips `archived` IN PLACE — no
  // remount — so a row shielded on the open tab was then filtered out of the
  // archive while the server-sourced total went on counting it. Nothing ever
  // cleared the set otherwise, not on refetch and not on tab return.
  useEffect(() => {
    recentlyMutatedIdsRef.current.clear();
  }, [archived]);
  // Accepting a request at a full event surfaces the 3-option capacity popup
  // (Auf Warteliste / Trotzdem als Gast / Abbrechen). This provider sits
  // OUTSIDE GuestsProvider on the Requests page, so it holds its own decision
  // state and renders the shared CapacityDecisionModal.
  const [capacityDecision, setCapacityDecision] = useState<{
    guestId: string;
    context?: CapacityContext;
    /**
     * The admin's "announce this again" opt-in from the re-decision dialog,
     * held across the capacity popup. The popup interrupts the write; the
     * resubmit is the same decision, so it has to carry the same choice —
     * dropping it here would silently swallow an opt-in the admin gave, which
     * is the exact dishonesty this dialog exists to remove.
     */
    retriggerAutomations?: boolean;
  } | null>(null);
  const [decisionLoading, setDecisionLoading] = useState(false);
  /**
   * The RAW failure of the last popup choice (M-10). The popup stays open on a
   * failure and translates this itself — closing it in `finally` made a refused
   * "add anyway" look exactly like one that went through. Reset in ONE place,
   * where the popup opens; a closed popup's stale value is never rendered.
   */
  const [decisionError, setDecisionError] = useState<unknown>(null);

  // Refetch deduped by REQUEST SIGNATURE (useSignatureEffect): fires only when
  // the request below would differ from what the host prefetched into
  // `initialRegistrants`. Replaces the boolean first-run guard (StrictMode
  // replay leak) and the raw `allFields` dep (identity churn). View-fields
  // stay out of the triggers on purpose (fixed projection — byte-identical
  // requests). NOTE: the parses here MUST stay inline/fresh —
  // getGuestsToBeReviewed mutates its filter argument (pushes a guestType
  // row), so sharing one parsed array with the fetch would grow it per call
  // and drift the signature into a fetch loop.
  const requestSignature = JSON.stringify({
    eventId,
    page,
    pageSize,
    archived,
    search,
    filters: parseFiltersApiRequest(activeFilterState.filter, allFields),
    sort: parseFieldsApiRequest(["createdAt"], allFields) ?? null,
  });
  signatureRef.current = requestSignature;
  if (mountSignatureRef.current === null) mountSignatureRef.current = requestSignature;
  // Monotonic request sequence: rapid signature changes (e.g. a blur-commit
  // immediately followed by the clear button) can resolve out of order — only
  // the latest request may commit its rows or its error.
  const requestSeqRef = useRef(0);
  // On a ref, not only inside the signature effect: a reconcile must be able to
  // re-run it with UNCHANGED params, which `useSignatureEffect` treats as a
  // no-op. Mirrors `GuestsProvider`'s `runFetchRef`; the recentlyMutated shield
  // and the out-of-order guard below come along unchanged.
  const runFetchRef = useRef<() => void>(() => {});
  runFetchRef.current = () => {
    const seq = ++requestSeqRef.current;
    startTransition(async () => {
      setFetchError(null);
      try {
        const registrantsData = await getGuestsToBeReviewed(
          eventId,
          page - 1,
          pageSize,
          parseFiltersApiRequest(activeFilterState.filter, allFields),
          parseFieldsApiRequest(["createdAt"], allFields),
          "desc",
          archived,
          search,
        );

        if (
          "isServerError" in registrantsData &&
          registrantsData.isServerError === true
        )
          throwServerError(registrantsData);

        if (seq !== requestSeqRef.current) return;
        const fresh = (registrantsData as Guest[]).filter(
          (g) => !recentlyMutatedIdsRef.current.has(g.guestId!),
        );
        setRegistrants(fresh);
      } catch (error) {
        if (seq !== requestSeqRef.current) return;
        setErrors((error as Error).message);
        setFetchError((error as Error).message);
        setRegistrants([]);
      }
    });
  };
  useSignatureEffect(requestSignature, () => {
    runFetchRef.current();
  });

  /**
   * Arm a reconcile for whatever an automation may rewrite behind this decision.
   *
   * Three things were wrong here and each of them made the wave silent:
   *
   * 1. The derived set was lifecycle triggers ONLY. The server also fires
   *    GUEST_UPDATED on every acceptance write from this page, so a
   *    GUEST_UPDATED + UPDATE_GUEST rule ran and nothing was ever armed for it.
   * 2. Only COMPANIONS were watched, on the premise — stated in the old comment
   *    — that "that row leaves this tab immediately". True on the open tab,
   *    false in the archive, where a re-decided row stays on screen and simply
   *    went stale.
   * 3. The companion watches could never satisfy. The nested companion DTOs
   *    carry only `_id`, `firstName` and `lastName` (GuestRepository builds them
   *    that way), so any other watched field had `fromRaw === undefined` and the
   *    scheduler polled for its full 45s deadline without ever firing.
   *
   * `guestType` is a literal: `getGuestsToBeReviewed` forces `mainGuest`, so
   * every row here is one — the projection doesn't even return the column.
   */
  const armRelatedReconcile = useCallback(
    (
      snapshot: Guest[],
      guestId: string,
      next: AcceptanceState,
      retriggered: boolean,
      /**
       * The request carried `forceWaitlist`, so the server may rewrite `state`
       * to WAITLISTED. Only the caller knows; `next` cannot say, because it is
       * an AcceptanceState and the rewrite lands on a different column.
       */
      ownWrite: { forceWaitlist?: boolean } = {},
    ) => {
      const row = snapshot.find((g) => g.guestId === guestId);
      if (!row) return;
      const triggers = new Set(
        deriveLifecycleTriggers(
          { acceptanceState: row.acceptanceState, guestType: GuestType.MAIN_GUEST },
          { acceptanceState: next },
          retriggered,
        ),
      );
      // The caller's own trigger. The server fires GUEST_UPDATED for EVERY write
      // from this page — its rule is `archived != null ? null : GUEST_UPDATED`,
      // and a reopen payload carries no `archived`. Suppressing it here on the
      // assumption that a reopen is silent left the derived set empty, and an
      // empty set makes `mayRun` hard-false, so a reopen armed nothing at all —
      // not even the coarse pulse.
      const explicit = explicitUpdateTrigger({});
      if (explicit) triggers.add(explicit);
      const triggerList = [...triggers];
      if (!mayRun(triggerList)) return;

      // Watch the fields the RULES target, not a fixed pair.
      // Drop what this write may itself move. `isSatisfied` is a `some`, so
      // one watch flipping on our own change ends the wave for this guest and
      // takes every correct sibling watch down with it.
      const fields = dropSelfWrittenFields(watchedFieldsFor(triggerList), ownWrite);
      if (fields.length === 0) {
        // Workflow list not loaded yet — pulse coarsely rather than start a
        // watch that could never satisfy.
        armCoarseReconcile(eventId);
        return;
      }
      const watches = new Map<string, FieldWatch[]>();
      // The re-decided row itself, whenever it stays on screen. Its baseline is
      // the value we JUST wrote, not the one we read — otherwise the first poll
      // satisfies on our own write and the wave ends before the automation runs.
      const rowStays = archived && next !== AcceptanceState.PENDING;
      if (rowStays) {
        watches.set(guestId, fields.map(({ field, isAdditional }) => ({
          field,
          isAdditional,
          // Everything here is untouched by this write —
          // `dropSelfWrittenFields` removed the rest — so the stored value is
          // a valid baseline. The `acceptanceState` branch that used to sit
          // here could never run: `watchedFieldsFor` cannot emit that field
          // non-additional, so `state` kept a baseline our own write moves.
          fromRaw: isAdditional
            ? (row.additionalFields as Record<string, unknown> | undefined)?.[field]
            : (row as unknown as Record<string, unknown>)[field],
        })));
      }
      // Companions can only be watched on the fields their projected DTO carries.
      const companionFields = fields.filter(
        ({ field, isAdditional }) => !isAdditional && COMPANION_DTO_FIELDS.has(field),
      );
      if (companionFields.length > 0) {
        for (const companion of row.companions ?? []) {
          if (!companion.guestId) continue;
          watches.set(
            companion.guestId,
            companionFields.map(({ field, isAdditional }) => ({
              field,
              isAdditional,
              fromRaw: (companion as unknown as Record<string, unknown>)[field],
            })),
          );
        }
      }
      if (watches.size === 0) {
        // Nothing here can be watched precisely — a rule targeting companions on
        // a field the projection drops, for instance. Pulse coarsely rather than
        // arm a wave that is guaranteed never to satisfy.
        armCoarseReconcile(eventId);
        return;
      }
      armGuestReconcile({ eventId, watches });
    },
    [eventId, archived, mayRun, watchedFieldsFor],
  );

  // An automation can change a guest rendered here after the response that
  // triggered it. Registrants are main guests by construction (the handler
  // forces `guestType = mainGuest`), so the reachable collateral is their
  // COMPANIONS — a `targetScope: "companions"` action writes them while the
  // approved row itself has already left this tab.
  useEffect(() => {
    const onReconciled = (evt: Event) => {
      const detail = (evt as CustomEvent<GuestReconciledDetail>).detail;
      if (detail.eventId !== eventId) return;
      setRegistrants((prev) => {
        let touched = false;
        const next = prev.map((row) => {
          if (row.guestId === detail.guestId) {
            touched = true;
            return applyServerTruth(row, detail.guest);
          }
          if (row.companions?.some((c) => c.guestId === detail.guestId)) {
            touched = true;
            return {
              ...row,
              companions: row.companions?.map((c) =>
                c.guestId === detail.guestId ? applyServerTruth(c, detail.guest) : c,
              ),
            };
          }
          return row;
        });
        return touched ? next : prev;
      });
      // Write-through: the cache must never lag local state. A reconciled value
      // that lived only in local state was silently reverted by the next cache
      // write's seed adopt — and died with the tab switch either way.
      applyServerTruthInRequestsCache(queryClient, eventId, detail.guestId, detail.guest);
    };
    const onCoarse = (evt: Event) => {
      const detail = (evt as CustomEvent<{ eventId: string }>).detail;
      if (detail.eventId !== eventId) return;
      if (signatureRef.current === mountSignatureRef.current) {
        // Un-diverged: refresh the SNAPSHOT and let the seed adopt carry it into
        // the grid, so the cache and the rows stay one truth. The adopt still
        // filters through the recentlyMutated shield.
        void refetchRequestsSnapshots(queryClient, eventId);
        return;
      }
      // Diverged view (paged/searched/refiltered): the host snapshot is page 1
      // and cannot describe what is on screen, so this view refreshes itself.
      runFetchRef.current();
    };
    window.addEventListener("guestReconciled", onReconciled);
    window.addEventListener("guestReconcileCoarse", onCoarse);
    return () => {
      window.removeEventListener("guestReconciled", onReconciled);
      window.removeEventListener("guestReconcileCoarse", onCoarse);
    };
  }, [eventId]);

  // An in-popup approve/decline (AcceptanceActions inside the guest-detail
  // popup) resolves a request OUTSIDE this provider — mirror the TCCAction
  // bookkeeping here so the row leaves and the tab badge/total drop without a
  // reload. The popup already invalidated the request caches.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (
        e as CustomEvent<{
          eventId?: string;
          guestId?: string;
          state?: AcceptanceState;
        }>
      ).detail;
      if (!detail?.guestId || !detail.state || detail.eventId !== eventId)
        return;
      if (!registrants.some((g) => g.guestId === detail.guestId)) return;
      // A reopen makes the row leave the ARCHIVE — the mirror image of a
      // decision leaving the open tab. Only then may it be shielded and counted.
      const reopened = detail.state === AcceptanceState.PENDING;
      if (archived === reopened) recentlyMutatedIdsRef.current.add(detail.guestId);
      if (archived && reopened) {
        setRegistrants((prev) => prev.filter((g) => g.guestId !== detail.guestId));
        setTotalCount((prev) => prev - 1);
        setCounts((prev) => ({ ...prev, registrant: prev.registrant + 1 }));
      } else if (archived) {
        setRegistrants((prev) =>
          prev.map((g) =>
            g.guestId === detail.guestId
              ? { ...g, acceptanceState: detail.state }
              : g,
          ),
        );
      } else {
        setRegistrants((prev) =>
          prev.filter((g) => g.guestId !== detail.guestId),
        );
        setTotalCount((prev) => prev - 1);
        setCounts((prev) => ({ ...prev, registrant: prev.registrant - 1 }));
      }
    };
    window.addEventListener("guestAcceptanceResolved", handler);
    return () => window.removeEventListener("guestAcceptanceResolved", handler);
  }, [eventId, archived, registrants, setTotalCount, setCounts]);

  // A field save in the guest-detail popup (its own isolated GuestsProvider)
  // broadcasts the confirmed patch — merge it into the matching row so the
  // table reflects the edit without a reload. Data-only: resolution (row
  // drop, counts, refetch shield) stays with guestAcceptanceResolved above.
  useEffect(() => {
    const handler = (e: Event) => {
      const detail = (e as CustomEvent<GuestDataUpdatedDetail>).detail;
      if (!detail?.guestId || !detail.patch || detail.eventId !== eventId)
        return;
      setRegistrants((prev) =>
        prev.map((g) =>
          g.guestId === detail.guestId ? mergeGuestPatch(g, detail.patch) : g,
        ),
      );
    };
    window.addEventListener("guestDataUpdated", handler);
    return () => window.removeEventListener("guestDataUpdated", handler);
  }, [eventId]);

  const createRegistrants = useCallback(async (guest: Guest) => {
    try {
      const response = await createPending(eventId, guest);

      if (
        typeof response === "object" &&
        "isServerError" in response &&
        response.isServerError === true
      ) {
        throwServerError(response);
      }

      const created = response as {
        guestIds: string[];
        guest?: Guest;
        outcome?: string;
      };
      const newId = created.guestIds?.[0];
      if (typeof newId === "string") {
        // Prefer the server-hydrated DTO over the form values, like the
        // companion twin: the form object has no acceptanceState/createdAt/
        // state/username and none of the server-applied additionalField
        // defaults, and this row is persisted into the snapshot the next mount
        // re-seeds from — a thin row there renders blank cells and makes the
        // detail popup's approve/decline disappear.
        const rowToInsert: Guest = created.guest
          ? { ...created.guest, guestId: newId }
          : { ...guest, guestId: newId };
        // The badge counts OPEN requests, so a new one always bumps it — even
        // while the admin is looking at the archive. The visible list and its
        // total, though, belong to whichever view is on screen: inserting a
        // fresh PENDING row into the ARCHIVE put a request into a list defined
        // as "already decided", where it also rendered with the wrong chip.
        setCounts((prev) => ({ ...prev, registrant: prev.registrant + 1 }));
        if (!archived) {
          setTotalCount((prev) => prev + 1);
          setRegistrants((prev) => [rowToInsert, ...prev]);
        }
        // Write the row THROUGH to the host's cached snapshot: this provider is
        // unmounted by a tab switch, and that snapshot is what re-seeds it.
        // NOT for a waitlisted create: the rule engine persists SYSTEM +
        // state=WAITLISTED, which `getGuestsToBeReviewed` excludes, so caching
        // it would pin a request that does not exist as a request (and inflate
        // the count) until a reload.
        if (isPendingRequestRow(rowToInsert)) {
          addGuestToRequestsCache(queryClient, eventId, "registrants", rowToInsert);
        }
        invalidateRequestCaches();
      }
    } catch (error) {
      // To the create dialog, which renders the reason (per-field violations
      // inline) and stays open — a toast here let `await onCreation()` resolve
      // and the dialog closed on a refusal (M-11). Same as GuestsProvider.addGuest.
      const message = (error as Error).message;
      setErrors(message);
      throw error;
    }
  }, [archived, eventId, setTotalCount, setCounts, invalidateRequestCaches, queryClient]);

  /**
   * The single write path for an acceptance decision on this tab.
   *
   * `leavesList` is the whole bookkeeping question. A row leaves the OPEN tab
   * when it gets decided, and leaves the ARCHIVE tab when it is reopened — the
   * two tabs are the same query split by state (`[PENDING]` vs
   * `[ACCEPTED, DECLINED]`), so membership changes in opposite directions.
   * Only a row that actually leaves may be removed, counted down, dropped from
   * the seed snapshot and shielded from the next refetch. Shielding an archive
   * RE-decision — which changes no membership at all — is what made a
   * just-edited archive row disappear on the next search or page change while
   * the footer went on counting it.
   */
  const decide = useCallback(
    async (
      guestId: string,
      next: AcceptanceState,
      opts?: { retriggerAutomations?: boolean },
    ): Promise<DecideOutcome> => {
      // Waitlist membership is server-owned (forceWaitlist / the promoter CAS)
      // and carries its own contingent bookkeeping. No generic path into it.
      if (next === AcceptanceState.SYSTEM) return null;
      const reopen = next === AcceptanceState.PENDING;
      // Reopening only means something for an already-decided row.
      if (reopen && !archived) return null;

      const snapshot = rowsRef.current;
      const index = snapshot.findIndex((g) => g.guestId === guestId);
      const previousRow = index >= 0 ? snapshot[index] : undefined;
      if (!previousRow) return null;
      if (previousRow.acceptanceState === next) return null;

      const leavesList = archived ? reopen : !reopen;

      if (leavesList) {
        setTotalCount((prev) => prev - 1);
        setRegistrants((prev) => prev.filter((g) => g.guestId !== guestId));
        recentlyMutatedIdsRef.current.add(guestId);
      }

      const rollback = () => {
        if (!leavesList) return;
        setRegistrants((prev) => restoreRow(prev, previousRow, index));
        setTotalCount((prev) => prev + 1);
        recentlyMutatedIdsRef.current.delete(guestId);
      };

      try {
        const payload: Guest = { guestId, acceptanceState: next };
        // Only send the opt-in when it is actually on, so the server's own
        // default is what runs and a test can assert the key's ABSENCE.
        if (opts?.retriggerAutomations) payload.retriggerAutomations = true;
        const response = await updateGuests(eventId, [payload]);

        if (
          typeof response === "object" &&
          response !== null &&
          "isServerError" in response &&
          response.isServerError === true
        ) {
          // Event full → roll back and open the 3-option capacity popup instead
          // of surfacing a raw error. Reachable on a re-approval too now that
          // the backend gates every entry into the counted set, not just a
          // first approval out of PENDING.
          const ctx = capacityContextOf(response);
          if (ctx && approvalBlockedGuestIds(response).includes(guestId)) {
            rollback();
            setDecisionError(null);
            setCapacityDecision({
              guestId,
              context: ctx,
              retriggerAutomations: opts?.retriggerAutomations === true,
            });
            // Not a success — the modal, not a toast, carries the next step.
            return null;
          }
          throwServerError(response);
        }

        if (leavesList) {
          if (reopen) {
            // The row goes back to the OPEN tab: its snapshot gains a row and
            // the tab badge — which counts open requests, never archived ones —
            // goes UP while this table's total goes down.
            setCounts((prev) => ({ ...prev, registrant: prev.registrant + 1 }));
            addGuestToRequestsCache(queryClient, eventId, "registrants", {
              ...previousRow,
              acceptanceState: AcceptanceState.PENDING,
            });
          } else {
            setCounts((prev) => ({ ...prev, registrant: prev.registrant - 1 }));
            dropGuestFromRequestsCache(queryClient, eventId, guestId, "registrants");
          }
        } else {
          // Archive re-decision: the row stays, so only its value changes.
          setRegistrants((prev) =>
            prev.map((g) => (g.guestId === guestId ? { ...g, acceptanceState: next } : g)),
          );
        }
        invalidateRequestCaches();
        armRelatedReconcile(snapshot, guestId, next, opts?.retriggerAutomations === true);
        // What the SERVER says it announced, not what we asked for.
        return { triggers: enqueuedTriggersFor(response, guestId) };
      } catch (error) {
        rollback();
        const message = (error as Error).message;
        setErrors(message);
        surfaceError(error);
        return null;
      }
    },
    [
      eventId,
      archived,
      setTotalCount,
      setCounts,
      surfaceError,
      invalidateRequestCaches,
      queryClient,
      armRelatedReconcile,
    ],
  );

  const acceptRegistrant = useCallback(
    (guestId: string) => decide(guestId, AcceptanceState.ACCEPTED),
    [decide],
  );
  // cap (overrideRules). Both remove it from the review list.
  const resubmitAccept = useCallback(
    async (guestId: string, decision: "waitlist" | "normal", retrigger = false) => {
      setDecisionLoading(true);
      const snapshot = registrants;
      if (!archived) {
        setTotalCount((prev) => prev - 1);
        setRegistrants((prev) => prev.filter((g) => g.guestId !== guestId));
      }
      // Only shield a row that actually left the list. In the archive the row is
      // kept and re-mapped in place, so shielding it there made it vanish on the
      // next refetch — the very defect `decide` was restructured to remove.
      if (!archived) recentlyMutatedIdsRef.current.add(guestId);
      try {
        const payload: Guest =
          decision === "waitlist"
            ? { guestId, acceptanceState: AcceptanceState.ACCEPTED, forceWaitlist: true }
            : { guestId, acceptanceState: AcceptanceState.ACCEPTED, overrideRules: true };
        // Only the override branch carries the opt-in. "Auf Warteliste" does
        // not approve anybody — the guest lands on the waitlist — so there is
        // no approval to announce and the server would fire nothing anyway.
        if (decision === "normal" && retrigger) payload.retriggerAutomations = true;
        const response = await updateGuests(eventId, [payload]);
        if (isServerErrorResponse(response)) {
          throwServerError(response);
        }
        // Went through: only now does the popup close (R3).
        setCapacityDecision(null);
        if (archived) {
          setRegistrants((prev) =>
            prev.map((guest) =>
              guest.guestId === guestId
                ? {
                    ...guest,
                    // "normal" (overrideRules) is deterministic: the server
                    // admits over the cap and stores ACCEPTED. "waitlist" is
                    // NOT — the reroute only fires while the capacity gate
                    // would still block, and capacity can free up between the
                    // dialog rendering and the click. Painting SYSTEM there
                    // put a waitlist badge on an accepted guest, against this
                    // file's own invariant that waitlist membership is
                    // server-owned. Leave the ambiguous branch alone and let
                    // the read-back below replace it with what was stored.
                    acceptanceState:
                      decision === "waitlist"
                        ? guest.acceptanceState
                        : AcceptanceState.ACCEPTED,
                  }
                : guest
            )
          );
        }
        if (!archived) {
          // The badge counts OPEN requests, and this row just left them.
          // Without this it keeps counting the guest until a reload — the twin
          // of the same omission in `decide`, which does call it.
          setCounts((prev) => ({ ...prev, registrant: prev.registrant - 1 }));
          dropGuestFromRequestsCache(queryClient, eventId, guestId, "registrants");
        }
        invalidateRequestCaches();
        // Same opt-in the payload carried — the reconcile watches the fields
        // the fired triggers touch, so telling it "nothing fired" here would
        // leave the cockpit blind to exactly the automation the admin asked for.
        armRelatedReconcile(
          snapshot, guestId, AcceptanceState.ACCEPTED,
          decision === "normal" && retrigger,
          { forceWaitlist: decision === "waitlist" },
        );
        // Only the ambiguous branch, and only when the row is still on screen.
        // `invalidateRequestCaches` uses `refetchType: "none"`, so without this
        // read nothing re-reads the row and the pre-write badge would simply
        // stay. One request, in exchange for never showing a verdict the
        // server did not give.
        if (archived && decision === "waitlist") {
          void readBackGuestVerdict(eventId, guestId).then((stored) => {
            if (!stored) return;
            setRegistrants((prev) =>
              prev.map((g) =>
                g.guestId === guestId
                  ? {
                      ...g,
                      acceptanceState: stored.acceptanceState,
                      // BOTH columns. The reroute moves `state` too, and
                      // applying only the acceptance half left the status cell
                      // showing the pre-write value next to a corrected badge.
                      ...(stored.state !== null ? { state: stored.state as typeof g.state } : {}),
                    }
                  : g,
              ),
            );
          });
        }
        // The write went through and may well have mailed. Saying nothing here
        // is the same silence the whole dialog exists to remove — it just moved
        // one modal further in.
        window.dispatchEvent(
          new CustomEvent("guestActionToast", {
            detail: {
              kind: archived ? "redecided" : "approved",
              retriggered: decision === "normal" && retrigger,
              triggers: enqueuedTriggersFor(response, guestId),
            },
          }),
        );
      } catch (error) {
        setRegistrants(snapshot);
        if (!archived) setTotalCount((prev) => prev + 1);
        recentlyMutatedIdsRef.current.delete(guestId);
        const message = (error as Error).message;
        setErrors(message);
        // In the popup, which stays open — no second channel (M-10).
        setDecisionError(error);
      } finally {
        setDecisionLoading(false);
      }
    },
    [eventId, archived, setTotalCount, registrants, invalidateRequestCaches, queryClient, armRelatedReconcile]
  );

  const declineRegistrant = useCallback(
    (guestId: string) => decide(guestId, AcceptanceState.DECLINED),
    [decide],
  );

  const contextValue = useMemo(() => ({
    errors, setErrors, fetchError, registrants, accept: acceptRegistrant, decline: declineRegistrant,
    decide, loading: isPending, createRegistrants,
  }), [errors, fetchError, registrants, acceptRegistrant, declineRegistrant, decide, isPending, createRegistrants]);

  return (
    <RegistrantsContext.Provider value={contextValue}>
      {children}
      {capacityDecision && (
        <CapacityDecisionModal
          context={capacityDecision.context}
          kind="accept"
          isLoading={decisionLoading}
          onWaitlist={() => resubmitAccept(capacityDecision.guestId, "waitlist")}
          onNormal={() =>
            resubmitAccept(
              capacityDecision.guestId,
              "normal",
              capacityDecision.retriggerAutomations === true,
            )
          }
          onCancel={() => setCapacityDecision(null)}
          error={decisionError ?? undefined}
        />
      )}
    </RegistrantsContext.Provider>
  );
};

export const useRegistrants = () => {
  const context = useContext(RegistrantsContext);
  return context;
};
